import { DT } from "../../constants.ts";
import { add, norm, scale, type Vec3 } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyHeal, applyImpulse, applyStatus, creditMitigation, damageBarrier, explode, isEnemy, moveProjectile, playersInRadius, spawnZone } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { release } from "../../sim/held.ts";
import { beginTargeting, endTargeting, targetConfirmed } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, zk, type HeroModule } from "../module.ts";
import { action, aura, commonInput, consume, grab, launchProjectile, ownerAlive, ready, secs, simpleProjectile, toGround, useUlt } from "../kitlib.ts";

export const V = { swim: 0, bubbles: 1, belly: 2, carried: 3, ultDiving: 4 } as const;
export const PK = { Spit: pk(HeroId.Geoff, 0), Burst: pk(HeroId.Geoff, 1) } as const;
export const ZK = { Bubble: zk(HeroId.Geoff, 0), SplashPool: zk(HeroId.Geoff, 1), BubbleBuff: zk(HeroId.Geoff, 2) } as const;
export const FX = { Water: fxk(HeroId.Geoff, 0), Chomp: fxk(HeroId.Geoff, 1), Bubble: fxk(HeroId.Geoff, 2) } as const;
const SWIM = MoveKind.Hero;
const BUBBLE_PICKUP_RADIUS = 1;
const BUBBLE_BUFF_RADIUS = 4;
const SWALLOW_RADIUS = 10;
const SWALLOW_HEIGHT = 5;
const ULT_DIVE_SECONDS = 6;

function grantBubbleBuff(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, center: Vec3) {
  for (const { eid: t } of playersInRadius(sim, center, BUBBLE_BUFF_RADIUS, (e) => !isEnemy(sim, owner, e), false)) {
    applyStatus(sim, t, "healBoost", 4, { source: owner, healBoostPct: 0.15 });
    applyStatus(sim, t, "haste", 4, { source: owner, hastePct: 0.6 });
    const z = spawnZone(sim, owner, ZK.BubbleBuff, sim.posOf(t), 0.1, 4);
    if (z !== undefined) sim.c.Zone.link[z] = t + 1;
  }
}

const def: HeroDef = {
  id: HeroId.Geoff,
  slug: "geoff",
  name: "Geoff the Land Shark",
  role: Role.Support,
  color: "#5fa8d3",
  blurb: "A tiny land shark who heals with happy spray, burrows as a fin, and swallows friend or foe before spitting them out.",
  health: 250,
  armor: 0,
  shields: 0,
  radius: 0.42,
  height: 1.08,
  headRadius: 0.25,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 4500,
  abilities: {
    [Slot.Primary]: { name: "Joyful Spritz", key: "LMB", description: "Spray water that heals allies and lightly damages enemies.", cooldown: 0 },
    [Slot.Secondary]: { name: "Aqua Belch", key: "RMB", description: "Lob a water burst that splashes enemies.", cooldown: 0 },
    [Slot.Ability1]: { name: "Hide and Snack", key: "Shift", description: "Dive underground as a quick fin, self-healing and resisting crowd control.", cooldown: 0 },
    [Slot.Ability2]: { name: "Friendly Bubble", key: "E", description: "Throw a healing bubble that buffs allies who touch it.", cooldown: 6, charges: 3 },
    [Slot.Ultimate]: { name: "It's Geoff!", key: "Q", description: "Dive for up to 6 seconds with a 10m swallow ring; press LMB to resurface and swallow allies and enemies.", cooldown: 0 },
  },
  passive: { name: "Oblivious Appetite", description: "Headshot damage is softened; Geoff is small and hard to pin down." },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.swim, 120);
      setHv(sim.c, eid, V.bubbles, 3);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      commonInput(sim, eid, input, pressed);
      if (hv(c, eid, V.ultDiving) > 0) {
        if (!canAct) {
          setHv(c, eid, V.ultDiving, 0);
          setHv(c, eid, V.belly, 0);
          endTargeting(sim, eid);
          return;
        }
        applyStatus(sim, eid, "phased", 0.2, { source: eid });
        applyStatus(sim, eid, "unstoppable", 0.2, { source: eid });
        applyStatus(sim, eid, "haste", 0.2, { hastePct: 0.6, source: eid });
        c.Move.kind[eid] = SWIM;
        c.Move.ticks[eid] = 2;
        action(sim, eid, ActionKind.Ult, 2);
        if (targetConfirmed(sim, eid, Slot.Ultimate)) {
          setHv(c, eid, V.ultDiving, 0);
          endTargeting(sim, eid);
          action(sim, eid, ActionKind.Ult, 30);
          let count = 0;
          const center = sim.posOf(eid);
          const centerY = center.y + sim.hero(eid).height * 0.5;
          for (const t of sim.playerEids()) {
            if (t === eid || !c.Player.alive[t]) continue;
            const p = sim.posOf(t);
            const targetY = p.y + sim.hero(t).height * 0.5;
            if (Math.hypot(p.x - center.x, p.z - center.z) > SWALLOW_RADIUS || Math.abs(targetY - centerY) > SWALLOW_HEIGHT / 2 || c.Status.unstoppable[t]! > 0) continue;
            grab(sim, eid, t, { offset: { x: 0, y: 0.45 + count * 0.08, z: -0.65 - count * 0.04 }, seconds: 6 });
            count++;
          }
          c.Ult.active[eid] = count > 0 ? secs(6) : 0;
          if (count === 0) action(sim, eid, ActionKind.Cast, 24);
          setHv(c, eid, V.carried, count);
          setHv(c, eid, V.belly, count > 0 ? 1 : 0);
        }
        return;
      }

      const diving = !!(input.buttons & Btn.Ability1) && hv(c, eid, V.swim) > 0 && canAct && c.Ult.active[eid]! <= 0;
      if (diving) {
        setHv(c, eid, V.swim, Math.max(0, hv(c, eid, V.swim) - 20 * DT));
        applyStatus(sim, eid, "unstoppable", 0.25, { source: eid });
        applyHeal(sim, eid, eid, 35 * DT, { noUlt: true, silent: true });
        c.Move.kind[eid] = SWIM;
        c.Move.ticks[eid] = 2;
        action(sim, eid, ActionKind.Cast, 2);
      } else setHv(c, eid, V.swim, Math.min(120, hv(c, eid, V.swim) + 15 * DT));
      if (!canAct) return;

      if (c.Ult.active[eid]! > 0) {
        const held = hv(c, eid, V.carried);
        setHv(c, eid, V.belly, c.Ult.active[eid]! / secs(6));
        for (const { eid: t } of playersInRadius(sim, sim.posOf(eid), 2.4, (e) => e !== eid, false)) {
          if (c.Status.heldBy[t]! === eid + 1) {
            if (isEnemy(sim, eid, t)) applyDamage(sim, t, 25 * DT, eid, { quiet: true });
            else {
              applyHeal(sim, eid, t, 225 * DT, { silent: true });
              c.Health.over[t] = Math.min(150, c.Health.over[t]! + 45 * DT);
              c.Health.overTicks[t] = secs(4);
            }
          }
        }
        if ((pressed & Btn.Primary) || c.Ult.active[eid] === 1) {
          release(sim, eid);
          setHv(c, eid, V.carried, 0);
          setHv(c, eid, V.belly, 0);
          c.Ult.active[eid] = 0;
          const a = aim(sim, eid, 1, false);
          const d = a.dir;
          for (const { eid: t } of playersInRadius(sim, sim.posOf(eid), 3, (e) => e !== eid, false)) if (c.Status.heldBy[t]! === 0 && held > 0) applyImpulse(sim, t, { x: d.x * 12, y: 4, z: d.z * 12 });
        }
        return;
      }

      if (useUlt(sim, eid, input, pressed)) {
        setHv(c, eid, V.ultDiving, 1);
        setHv(c, eid, V.carried, 0);
        setHv(c, eid, V.belly, 0);
        spawnZone(sim, eid, ZK.SplashPool, sim.posOf(eid), 8, 8);
        beginTargeting(sim, eid, Slot.Ultimate);
        action(sim, eid, ActionKind.Ult, 30);
        return;
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 10);
        const a = aim(sim, eid, 25, false);
        spawnZone(sim, eid, ZK.Bubble, toGround(sim, add(a.origin, scale(a.dir, 6))), BUBBLE_PICKUP_RADIUS, 180);
      }
      if (input.buttons & Btn.Secondary && c.Weapon.fireCd[eid]! <= 0) {
        c.Weapon.fireCd[eid] = secs(1 / 1.75);
        action(sim, eid, ActionKind.AltFire, 10);
        launchProjectile(sim, eid, PK.Burst, 150, input, { radius: 0.2, gravity: 8, seconds: 1.5 });
      } else if (input.buttons & Btn.Primary && c.Weapon.fireCd[eid]! <= 0) {
        c.Weapon.fireCd[eid] = 3;
        action(sim, eid, ActionKind.Fire, 4);
        launchProjectile(sim, eid, PK.Spit, 100, input, { radius: 0.18, seconds: 0.3 });
      }
    },
    movement(sim, eid, input, mods) {
      if (sim.c.Move.kind[eid] === SWIM || (input.buttons & Btn.Ability1 && hv(sim.c, eid, V.swim) > 0)) {
        mods.speedMul *= 1.6;
        mods.gravityMul = 0.3;
      }
    },
    scripted(sim, eid, input) {
      const c = sim.c;
      const a = aim(sim, eid, 1, false);
      const dir = norm({ x: a.dir.x + input.moveX * 0.2, y: 0, z: a.dir.z + input.moveZ * 0.2 });
      c.Vel.x[eid] = dir.x * 9.6;
      c.Vel.z[eid] = dir.z * 9.6;
      c.Vel.y[eid] = Math.min(c.Vel.y[eid]!, 0.5);
    },
    onDamaged(sim, eid, amount, _attacker, info) {
      if (!info.crit) return amount;
      const reduced = amount * 0.75;
      creditMitigation(sim, eid, amount - reduced);
      return reduced;
    },
  },
  projectiles: {
    [PK.Spit]: {
      visual: { color: "#8be8ff", size: 0.16, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "both");
        if (hit) {
          if (hit.target !== undefined && ownerAlive(sim, owner)) {
            if (isEnemy(sim, owner, hit.target)) {
              applyDamage(sim, hit.target, 3.5, owner, { pos: hit.pos });
              applyImpulse(sim, hit.target, { x: 0, y: 0.8, z: 0 });
            } else applyHeal(sim, owner, hit.target, 7);
          } else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 8, owner);
          else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 8, owner);
          sim.emit({ type: "impact", pos: hit.pos, kind: FX.Water });
          sim.destroyEntity(p);
        } else if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
    [PK.Burst]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        explode(sim, owner, hit.pos, { radius: 3, damage: 45, minDamage: 22.5, knockback: 4, fxKind: FX.Water });
      },
      { color: "#39cfff", size: 0.24, trail: true },
    ),
  },
  zones: {
    [ZK.Bubble]: {
      visual: { color: "#8be8ff", dome: true },
      tick(sim, z) {
        const c = sim.c;
        const owner = c.Zone.owner[z]!;
        const hit = playersInRadius(sim, sim.posOf(z), c.Zone.radius[z]!, (e) => !isEnemy(sim, owner, e), false)[0];
        if (hit) {
          grantBubbleBuff(sim, owner, sim.posOf(hit.eid));
          sim.emit({ type: "impact", pos: sim.posOf(z), kind: FX.Bubble });
          sim.destroyEntity(z);
        }
      },
    },
    [ZK.BubbleBuff]: {
      visual: { color: "#8be8ff", dome: false },
      tick(sim, z) {
        const c = sim.c;
        const owner = sim.c.Zone.owner[z]!;
        const target = c.Zone.link[z]! - 1;
        if (target < 0 || !c.Player.alive[target] || isEnemy(sim, owner, target)) {
          sim.destroyEntity(z);
          return;
        }
        const p = sim.posOf(target);
        c.Pos.x[z] = p.x;
        c.Pos.y[z] = p.y;
        c.Pos.z[z] = p.z;
        applyHeal(sim, owner, target, 30 * DT, { silent: true });
      },
    },
    [ZK.SplashPool]: {
      visual: { color: "#65dbff", dome: false },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        aura(sim, owner, sim.posOf(z), { radius: sim.c.Zone.radius[z]!, healAlliesPerSec: 130, los: false });
      },
    },
  },
  fx: {
    [FX.Water]: { color: "#7fdfff" },
    [FX.Chomp]: { color: "#9ff2ff" },
    [FX.Bubble]: { color: "#c9fbff" },
  },
  targets: {
    [Slot.Ultimate]: { range: 0, radius: SWALLOW_RADIUS, start: "kit", self: true, timeout: ULT_DIVE_SECONDS, verb: "Resurface" },
  },
};

export default module;
